import React, { useEffect, useMemo } from "react";
import Label from "./LabelF";
import { getAtributo } from "../../../context/FuncionesContext";

// Admite campos simples y rutas de una opción del autocomplete, por ejemplo:
// PRODUCTO_op.upotenci
const IDENTIFIER_REGEX =
  /\b[A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*\b/g;
const SAFE_EXPRESSION_REGEX = /^[\d\s(),+\-*/%.A-Za-z_]+$/;
const FIELD_PATH_REGEX = /^[A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*$/;
const DATEDIFF_REGEX =
  /\bDATEDIFF\s*\(\s*([A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*)\s*,\s*([A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*)\s*\)/gi;
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

const parseNumericValue = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim().replace(",", ".");
  if (normalized === "") {
    return null;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const getValorFormulario = (valorFormulario, token) => {
  if (Object.prototype.hasOwnProperty.call(valorFormulario, token)) {
    return valorFormulario[token];
  }

  return token.split(".").reduce((valorActual, key) => {
    if (valorActual === null || typeof valorActual !== "object") {
      return undefined;
    }

    if (Object.prototype.hasOwnProperty.call(valorActual, key)) {
      return valorActual[key];
    }

    const keyCoincidente = Object.keys(valorActual).find(
      (clave) => clave.toLowerCase() === key.toLowerCase()
    );

    return keyCoincidente ? valorActual[keyCoincidente] : undefined;
  }, valorFormulario);
};

const parseDateValue = (value) => {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return Date.UTC(value.getFullYear(), value.getMonth(), value.getDate());
  }

  if (typeof value !== "string") return null;

  const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;

  const [, year, month, day] = match;
  const timestamp = Date.UTC(Number(year), Number(month) - 1, Number(day));
  const parsed = new Date(timestamp);

  if (
    parsed.getUTCFullYear() !== Number(year) ||
    parsed.getUTCMonth() !== Number(month) - 1 ||
    parsed.getUTCDate() !== Number(day)
  ) {
    return null;
  }

  return timestamp;
};

const resolverDatediff = (expresion, valorFormulario) => {
  let faltanValores = false;
  let encontroDatediff = false;

  const expresionResuelta = expresion.replace(
    DATEDIFF_REGEX,
    (_coincidencia, fechaFinToken, fechaInicioToken) => {
      encontroDatediff = true;
      const fechaFin = parseDateValue(
        getValorFormulario(valorFormulario, fechaFinToken)
      );
      const fechaInicio = parseDateValue(
        getValorFormulario(valorFormulario, fechaInicioToken)
      );

      if (fechaFin === null || fechaInicio === null) {
        faltanValores = true;
        return "0";
      }

      return String(Math.trunc((fechaFin - fechaInicio) / MILLISECONDS_PER_DAY));
    }
  );

  return {
    expresion: expresionResuelta,
    invalida: encontroDatediff && faltanValores,
  };
};

const resolverExpresion = (expresion, valorFormulario) => {
  if (!expresion || typeof expresion !== "string") {
    return "";
  }

  if (!SAFE_EXPRESSION_REGEX.test(expresion)) {
    return "";
  }

  // Una referencia sin operadores puede mostrar tanto valores numéricos como
  // texto (por ejemplo, PRODUCTO_op.label). Las fórmulas continúan siendo
  // exclusivamente numéricas.
  if (FIELD_PATH_REGEX.test(expresion)) {
    const valor = getValorFormulario(valorFormulario, expresion);
    return typeof valor === "string" || typeof valor === "number" ? valor : "";
  }

  const datediff = resolverDatediff(expresion, valorFormulario);
  if (datediff.invalida) return "";

  let faltanValores = false;

  const expresionResuelta = datediff.expresion.replace(IDENTIFIER_REGEX, (token) => {
    const value = getValorFormulario(valorFormulario, token);
    const numericValue = parseNumericValue(value);

    if (numericValue === null) {
      faltanValores = true;
      return "0";
    }

    return String(numericValue);
  });

  if (faltanValores) {
    return "";
  }

  try {
    const result = Function(`"use strict"; return (${expresionResuelta});`)();

    if (!Number.isFinite(result)) {
      return "";
    }

    return result;
  } catch (error) {
    return "";
  }
};

const aplicarRound = (resultado, round) => {
  if (resultado === "" || resultado === null || resultado === undefined) {
    return "";
  }

  if (round === undefined || round === null || round === "") {
    return resultado;
  }

  const decimales = Number.parseInt(round, 10);
  if (Number.isNaN(decimales)) {
    return resultado;
  }

  const numero = Number(resultado);
  return Number.isFinite(numero) ? numero.toFixed(decimales) : resultado;
};

const ValorCalculado = ({
  cab,
  data,
  valor,
  setValor,
  valorFormulario,
}) => {
  const formula =
    getAtributo("valor_calculado", data, cab) ??
    getAtributo("formula_calculada", data, cab);
  const round = getAtributo("round", data, cab);
  const className = getAtributo("className", data, cab);

  const resultado = useMemo(() => {
    const valorResuelto = resolverExpresion(formula, valorFormulario ?? {});
    return aplicarRound(valorResuelto, round);
  }, [formula, round, valorFormulario]);

  useEffect(() => {
    if (resultado === "" && (valor === "" || valor === undefined || valor === null)) {
      return;
    }

    if (valor !== resultado) {
      setValor(resultado);
    }
  }, [resultado, setValor, valor]);

  return (
    <>
      {cab.nombre ? (
        <Label
          label={cab.nombre}
          opcionales_null={cab.opcionales_null}
          permite_null={cab.permite_null}
        />
      ) : null}
      <div
        className={className}
        style={{
          width: cab.width,
        }}
      >
        <div style={{ textAlign: cab.align }}>{resultado ?? ""}</div>
      </div>
    </>
  );
};

export default ValorCalculado;
